import { logger } from '../utils/logger';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface LoginResponse {
  token: string;
  userId: string;
  email: string;
  displayName?: string;
  expiresIn: number;
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  userId: string;
  email: string;
  organization: any;
}

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    logger.debug('[Login] Calling backend to authenticate...');
    
    const response = await fetch(`${apiUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim(),
        password: password,
      }),
    });

    logger.debug('[Login] Backend response status:', response.status);

    if (!response.ok) {
      let errorMessage = 'Erro ao fazer login';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
        console.error('[Login] Backend error:', errorData);
      } catch (e) {
        console.error('[Login] Error parsing error response:', e);
        const text = await response.text().catch(() => '');
        console.error('[Login] Response text:', text);
        if (response.status === 401) {
          errorMessage = 'Email ou senha inválidos';
        } else if (response.status === 404) {
          errorMessage = 'Serviço não encontrado. Verifique a configuração da API.';
        } else if (response.status === 500) {
          errorMessage = 'Erro interno do servidor. Tente novamente em alguns instantes.';
        }
      }
      console.error('[Login] Backend returned error:', response.status, errorMessage);
      throw new Error(errorMessage);
    }

    const responseData: LoginResponse = await response.json();
    logger.debug('[Login] Login successful');
    
    return responseData;
  } catch (error: any) {
    console.error('[Login] Error in login function:', error);
    throw error;
  }
};

export const register = async (email: string, password: string, organizationName: string): Promise<RegisterResponse> => {
  try {
    logger.debug('[Register] Calling backend to create user and organization...');
    
    logger.debug('[Register] API URL:', apiUrl);
    const response = await fetch(`${apiUrl}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim(),
        password: password,
        organizationName: organizationName.trim(),
      }),
    });

    logger.debug('[Register] Backend response status:', response.status);

    if (!response.ok) {
      let errorMessage = 'Erro ao criar conta';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
        console.error('[Register] Backend error:', errorData);
      } catch (e) {
        console.error('[Register] Error parsing error response:', e);
        const text = await response.text().catch(() => '');
        console.error('[Register] Response text:', text);
        if (response.status === 401) {
          errorMessage = 'Erro de autenticação. Tente novamente.';
        } else if (response.status === 404) {
          errorMessage = 'Serviço não encontrado. Verifique a configuração da API.';
        } else if (response.status === 500) {
          errorMessage = 'Erro interno do servidor. Tente novamente em alguns instantes.';
        }
      }
      console.error('[Register] Backend returned error:', response.status, errorMessage);
      throw new Error(errorMessage);
    }

    const responseData: RegisterResponse = await response.json();
    logger.debug('[Register] Account created successfully');
    
    return responseData;
  } catch (error: any) {
    console.error('[Register] Error in register function:', error);
    throw error;
  }
};

export const registerWithInvite = async (
  email: string, 
  password: string, 
  inviteCode: string,
  displayName?: string
): Promise<LoginResponse> => {
  try {
    logger.debug('[RegisterWithInvite] Calling backend to create user with invite code...');
    
    const response = await fetch(`${apiUrl}/auth/register-with-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim(),
        password: password,
        inviteCode: inviteCode.trim().toUpperCase(),
        displayName: displayName?.trim(),
      }),
    });

    logger.debug('[RegisterWithInvite] Backend response status:', response.status);

    if (!response.ok) {
      let errorMessage = 'Erro ao criar conta com convite';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
        console.error('[RegisterWithInvite] Backend error:', errorData);
      } catch (e) {
        console.error('[RegisterWithInvite] Error parsing error response:', e);
        const text = await response.text().catch(() => '');
        console.error('[RegisterWithInvite] Response text:', text);
        if (response.status === 400) {
          errorMessage = 'Código de convite inválido ou expirado';
        } else if (response.status === 404) {
          errorMessage = 'Serviço não encontrado. Verifique a configuração da API.';
        } else if (response.status === 500) {
          errorMessage = 'Erro interno do servidor. Tente novamente em alguns instantes.';
        }
      }
      console.error('[RegisterWithInvite] Backend returned error:', response.status, errorMessage);
      throw new Error(errorMessage);
    }

    const responseData: LoginResponse = await response.json();
    logger.debug('[RegisterWithInvite] Account created successfully');
    
    return responseData;
  } catch (error: any) {
    console.error('[RegisterWithInvite] Error in registerWithInvite function:', error);
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  try {
    // Chamar endpoint de logout no backend (opcional)
    const token = localStorage.getItem('jwt_token');
    if (token) {
      try {
        await fetch(`${apiUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('[Logout] Error calling backend logout:', error);
        // Continuar mesmo se o backend falhar
      }
    }
  } catch (error) {
    console.error('[Logout] Error in logout function:', error);
    // Continuar mesmo se houver erro
  }
};
